import React, { useMemo, useRef, useState } from "react";
import { IconGrid, IconList } from "../components/ui/icons";
import Select from "../components/ui/Select";
import ExportButtonGroup from "../components/ui/ExportButtonGroup";
import LoadingOverlay from "../components/ui/LoadingOverlay";
import { LABEL } from "../components/ui/controlStyles";
import { ListSkeleton, Skeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/StateBlock";
import Card from "../components/ui/Card";
import Modal from "../components/ui/Modal";
import { formatWaiters } from "../utils/format";
import OccupancyMeter from "../components/OccupancyMeter";
import TableMap, { REGION_NAMES } from "../components/TableMap";
import { useApiData } from "../hooks/useApiData";
import { apiFetch } from "../utils/api";
import { exportSectionsToExcel, exportSectionsToPDF, printSections } from "../utils/exportUtils";

const segClass = (active) =>
  `inline-flex h-8 items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors ${
    active
      ? "bg-surface text-slate-900 shadow-card dark:bg-slate-700 dark:text-white"
      : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
  }`;

const STATUS_OPTIONS = [
  { value: "svi", label: "Svi" },
  { value: "zauzeti", label: "Zauzeti" },
  { value: "slobodni", label: "Slobodni" },
];

export default function TableStatus() {
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("zauzeti");
  const [details, setDetails] = useState(null);
  const [view, setView] = useState("mapa");
  const tableElementRef = useRef(null);

  const { data: regionsData } = useApiData("/regions.php");
  const regionList = regionsData?.success ? regionsData.regions : [];

  const tablesPath = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedRegion) params.append("rejon", selectedRegion);
    // The status is not sent to the server: it is filtered here, so the counters (Zauzet/Slobodan) stay constant.
    return `/tables.php?${params.toString()}`;
  }, [selectedRegion]);

  const [retryTick, setRetryTick] = useState(0);
  const { data: tablesData, loading, error } = useApiData(tablesPath, retryTick);
  const hasError = Boolean(error) || (tablesData && tablesData.success === false);
  const allTables = tablesData?.success ? tablesData.tables.map((s) => ({ ...s, Zauzet: s.Zauzet === 1 })) : [];
  // Counters apply to the whole selected region and do not depend on the Status filter.
  const occupiedCount = allTables.filter((s) => s.Zauzet).length;
  const freeCount = allTables.length - occupiedCount;
  const tables = allTables.filter((s) =>
    selectedStatus === "zauzeti" ? s.Zauzet : selectedStatus === "slobodni" ? !s.Zauzet : true
  );

  const handleTableClick = async (table) => {
    if (!table.Zauzet || !table.ID) {
      setDetails(null);
      return;
    }
    try {
      const res = await apiFetch(`/table_details.php?id=${table.ID}`);
      setDetails(await res.json());
    } catch (err) {
      console.error("Greška pri učitavanju detalja stola:", err);
    }
  };

  const totalValue = details?.stavke?.reduce((sum, s) => sum + s.vrednost, 0) || 0;
  const totalQuantity = details?.stavke?.reduce((sum, s) => sum + s.kolicina, 0) || 0;

  // Exports always cover the occupied tables of the selected region (regardless of the Status filter), with their items.
  const occupiedTables = allTables.filter((t) => t.Zauzet && t.ID);

  async function fetchOccupiedDetails() {
    // Requests run in parallel (the order of results is preserved); the server no longer serializes them.
    const all = await Promise.all(
      occupiedTables.map(async (table) => {
        try {
          const res = await apiFetch(`/table_details.php?id=${table.ID}`);
          const json = await res.json();
          return json && json.stavke ? { ...json, sto: json.sto || table.Sto, konobar: json.konobar || table.konobar || "" } : null;
        } catch (err) {
          console.error(`Greška pri učitavanju stola ${table.Sto}:`, err);
          return null;
        }
      })
    );
    return all.filter((d) => d && d.stavke.length > 0);
  }

  // "2026-10-08 21:15:03" -> "21:15" (with the date when it is not today)
  const formatOpened = (value) => {
    const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return "";
    const now = new Date();
    const sameDay = Number(m[1]) === now.getFullYear() && Number(m[2]) === now.getMonth() + 1 && Number(m[3]) === now.getDate();
    return sameDay ? `${m[4]}:${m[5]}` : `${m[3]}.${m[2]}. ${m[4]}:${m[5]}`;
  };

  // One section per table (items, bold subtotal, waiter and time) and a grand total.
  function buildTableSections(allDetails) {
    let grandValue = 0;
    let grandQty = 0;
    const sections = allDetails.map((d) => {
      const items = d.stavke.map((s) => ({
        name: s.naziv,
        round: s.tura === null || s.tura === undefined ? "" : String(s.tura),
        qty: Number(s.kolicina) || 0,
        price: Number(s.cena) || 0,
        value: Number.isFinite(Number(s.vrednost)) ? Number(s.vrednost) : (Number(s.kolicina) || 0) * (Number(s.cena) || 0),
      }));
      const qty = items.reduce((a, i) => a + i.qty, 0);
      const value = items.reduce((a, i) => a + i.value, 0);
      grandQty += qty;
      grandValue += value;
      const wholeQty = items.every((i) => Number.isInteger(i.qty));
      const opened = formatOpened(d.otvoren);
      return {
        heading: `Sto ${d.sto}`,
        meta: [`Konobar: ${formatWaiters(d.konobar) || "-"}`, ...(opened ? [`Otvoren: ${opened}`] : [])],
        head: ["Artikal", "Tura", "Kol", "Cena", "Vrednost"],
        body: items.map((i) => [i.name, i.round, i.qty, i.price, i.value]),
        totalRow: [`Ukupno sto ${d.sto}`, "", qty, "", value],
        decimals: [null, null, wholeQty ? 0 : 2, 2, 2],
        columnStyles: { 1: { cellWidth: 40 } },
      };
    });
    return {
      sections,
      grandRow: [`Ukupno svi stolovi (${sections.length})`, "", grandQty, "", grandValue],
      grandDecimals: [null, null, Number.isInteger(grandQty) ? 0 : 2, null, 2],
    };
  }

  const exportAllTablesPDF = async () => {
    const { sections, grandRow, grandDecimals } = buildTableSections(await fetchOccupiedDetails());
    if (!sections.length) return;
    exportSectionsToPDF({ title: "Zauzeti stolovi", sections, grandRow, grandDecimals, filename: "zauzeti_stolovi.pdf" });
  };

  const printAllTables = async () => {
    const { sections, grandRow, grandDecimals } = buildTableSections(await fetchOccupiedDetails());
    if (!sections.length) return;
    printSections({ title: "Zauzeti stolovi", sections, grandRow, grandDecimals });
  };

  const exportAllTablesExcel = async () => {
    const { sections, grandRow, grandDecimals } = buildTableSections(await fetchOccupiedDetails());
    if (!sections.length) return;
    exportSectionsToExcel({ title: "Zauzeti stolovi", sections, grandRow, grandDecimals, filename: "zauzeti_stolovi.xlsx", sheetName: "Stolovi" });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-surface p-3 dark:border-slate-800 dark:bg-slate-900 lg:flex-row lg:flex-wrap lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-3">
          <Select
            label="Rejon"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            options={[{ value: "", label: "Svi" }, ...regionList.map((r) => ({ value: r.id, label: r.name }))]}
          />
          <Select
            label="Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
          <div className="flex flex-col gap-1.5">
            <span className={LABEL}>Prikaz</span>
            <div
              role="group"
              aria-label="Prikaz"
              className="inline-flex rounded-md border border-slate-300 bg-slate-100 p-0.5 dark:border-slate-700 dark:bg-slate-900"
            >
              <button type="button" aria-pressed={view === "mapa"} onClick={() => setView("mapa")} className={segClass(view === "mapa")}>
                <IconGrid aria-hidden="true" className="text-base" /> Mapa
              </button>
              <button type="button" aria-pressed={view === "lista"} onClick={() => setView("lista")} className={segClass(view === "lista")}>
                <IconList aria-hidden="true" className="text-base" /> Lista
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3 dark:border-slate-800 lg:border-0 lg:pt-0">
          <ExportButtonGroup
            onExcel={exportAllTablesExcel}
            onPdf={exportAllTablesPDF}
            onPrint={printAllTables}
            disabled={occupiedCount === 0}
            size="sm"
          />
        </div>
      </div>

      <Card padded={view === "mapa"}>
        {hasError ? (
          <ErrorState message="Stolovi nisu učitani. Proveri vezu i pokušaj ponovo." onRetry={() => setRetryTick((t) => t + 1)} />
        ) : loading && allTables.length === 0 ? (
          view === "mapa" ? (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" role="status" aria-label="Učitavanje">
              <Skeleton className="aspect-[2/1] w-full rounded-lg" />
              <Skeleton className="aspect-[2/1] w-full rounded-lg" />
            </div>
          ) : (
            <div className="p-4">
              <ListSkeleton rows={6} />
            </div>
          )
        ) : view === "mapa" ? (
          <>
            <p className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm border border-danger/70 bg-danger/20" />
                Zauzet ({occupiedCount})
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm border border-success/70 bg-success/20" />
                Slobodan ({freeCount})
              </span>
              <span>Klik na zauzet sto otvara račun.</span>
            </p>
            {allTables.length > 0 && (
              <div className="mb-3 flex max-w-xs items-center gap-3">
                <span className="text-xs text-slate-500 dark:text-slate-400">Zauzetost</span>
                <OccupancyMeter pct={(occupiedCount / allTables.length) * 100} className="flex-1" />
              </div>
            )}
            <LoadingOverlay loading={loading}>
              <TableMap tables={tables} allTables={allTables} onSelect={handleTableClick} />
            </LoadingOverlay>
          </>
        ) : (
          <LoadingOverlay loading={loading}>
            <table ref={tableElementRef} className="w-full table-fixed border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" style={{ width: "48%" }}
                    className="sticky top-0 rounded-tl-[9px] border-b border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs font-medium text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                    Sto
                  </th>
                  <th scope="col" style={{ width: "28%" }} className="sticky top-0 border-b border-slate-200 bg-slate-50 px-2 py-2 text-right text-xs font-medium text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 sm:px-3">
                    Vrednost
                  </th>
                  <th scope="col" style={{ width: "24%" }} className="sticky top-0 rounded-tr-[9px] border-b border-slate-200 bg-slate-50 px-2 py-2 text-right sm:px-3 text-xs font-medium text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {tables.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <EmptyState title="Nema stolova za izabrani filter" hint="Promeni rejon ili status iznad." />
                    </td>
                  </tr>
                )}
                {[...tables]
                  .sort((a, b) => a.Region - b.Region || a.Sto.localeCompare(b.Sto))
                  .map((table) => (
                    <tr
                      key={table.ID}
                      onClick={() => handleTableClick(table)}
                      className={`transition-colors ${table.Zauzet ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" : ""}`}
                    >
                      <td className="px-3 py-2">
                        {table.Zauzet ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTableClick(table);
                            }}
                            className="rounded-sm font-medium text-slate-900 hover:text-primary hover:underline dark:text-slate-50 dark:hover:text-primary-light"
                          >
                            {table.Sto}
                          </button>
                        ) : (
                          <span className="font-medium text-slate-900 dark:text-slate-50">{table.Sto}</span>
                        )}
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400 sm:ml-2 sm:inline">{REGION_NAMES[table.Region]}</span>
                      </td>
                      <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200 sm:px-3">{table.Vrednost}</td>
                      <td className="px-2 py-2 text-right sm:px-3">
                        <span
                          className={`inline-flex whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${
                            table.Zauzet
                              ? "bg-danger/10 text-danger-dark dark:text-danger-light"
                              : "bg-success/10 text-success-dark dark:text-success-light"
                          }`}
                        >
                          {table.Zauzet ? "Zauzet" : "Slobodan"}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </LoadingOverlay>
        )}
      </Card>

      <Modal open={Boolean(details)} onClose={() => setDetails(null)} title={details ? `Sto: ${details.sto}` : ""}>
        {details && (
          <>
            <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
              Konobar: {formatWaiters(details.konobar)}
            </p>

            {/* Phone: each item on its own row (name, "qty × price", value) - columns no longer run together */}
            <div className="sm:hidden">
              {details.stavke?.map((s, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-3 border-b border-slate-100 py-2.5 dark:border-slate-700/60"
                >
                  <div className="min-w-0">
                    <div className="break-words text-sm text-slate-800 dark:text-slate-100">{s.naziv}</div>
                    <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {s.kolicina} × {s.cena.toFixed(2)}, tura {s.tura}
                    </div>
                  </div>
                  <div className="tabular-nums shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-50">
                    {(s.kolicina * s.cena).toFixed(2)}
                  </div>
                </div>
              ))}
              <div className="mt-1 flex items-center justify-between border-t-2 border-slate-200 pt-3 text-sm font-semibold text-slate-900 dark:border-slate-600 dark:text-slate-50">
                <span>Ukupno: {totalQuantity.toFixed(2)}</span>
                <span className="tabular-nums">{totalValue.toFixed(2)}</span>
              </div>
            </div>

            <table className="hidden w-full border-collapse text-sm sm:table">
              <thead>
                <tr>
                  {["Artikal", "Kol", "Cena", "Vrednost", "Tura"].map((h, i) => (
                    <th
                      key={h}
                      className={`border-b border-slate-200 py-2 font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300 ${
                        i === 0 ? "text-left" : i === 4 ? "text-center" : "text-right"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {details.stavke?.map((s, idx) => (
                  <tr key={idx} className="border-b border-slate-100 last:border-0 dark:border-slate-700/60">
                    <td className="py-2 text-slate-700 dark:text-slate-200">{s.naziv}</td>
                    <td className="tabular-nums py-2 text-right text-slate-700 dark:text-slate-200">{s.kolicina}</td>
                    <td className="tabular-nums py-2 text-right text-slate-700 dark:text-slate-200">{s.cena.toFixed(2)}</td>
                    <td className="tabular-nums py-2 text-right text-slate-700 dark:text-slate-200">
                      {(s.kolicina * s.cena).toFixed(2)}
                    </td>
                    <td className="py-2 text-center text-slate-700 dark:text-slate-200">{s.tura}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-semibold dark:border-slate-600">
                  <td className="py-2.5 text-slate-900 dark:text-slate-50">Ukupno:</td>
                  <td className="tabular-nums py-2.5 text-right text-slate-900 dark:text-slate-50">
                    {totalQuantity.toFixed(2)}
                  </td>
                  <td />
                  <td className="tabular-nums py-2.5 text-right text-slate-900 dark:text-slate-50">
                    {totalValue.toFixed(2)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </Modal>
    </div>
  );
}
